/**
 * Compute the absolute-positioning style for a CSS-cropped face cutout
 * inside a fixed-size box.
 *
 * Uses the face's `frame_bbox` (source-pixel space) + the photo's
 * `image_width / image_height`. The face's `preview_url` resolves to
 * `__preview.jpg`, which is a downscale of the SOURCE image — so
 * source-space coordinates are the right reference. (Don't use `bbox`
 * + `crop_width/height` — those live in cropped-output space and won't
 * align to the preview.)
 *
 * Returns null when the face is missing the geometry needed to position
 * the cutout; the caller should fall back to a placeholder.
 */
export function computeFaceCropStyle(face, boxW, boxH) {
  const fb = face?.frame_bbox;
  const srcW = face?.image_width;
  const srcH = face?.image_height;
  if (!Array.isArray(fb) || fb.length !== 4 || !srcW || !srcH) return null;
  const [x1, y1, x2, y2] = fb;
  const faceW = x2 - x1;
  const faceH = y2 - y1;
  if (faceW <= 0 || faceH <= 0) return null;
  const faceCx = x1 + faceW / 2;
  const faceCy = y1 + faceH / 2;
  // 1.3× padding around the face — matches FaceCard.jsx convention.
  const padding = 1.3;
  const fitTo = Math.min(boxW, boxH);
  const scale = fitTo / (Math.max(faceW, faceH) * padding);
  return {
    width: srcW * scale,
    height: srcH * scale,
    left: boxW / 2 - faceCx * scale,
    top: boxH / 2 - faceCy * scale,
  };
}
